import React from 'react';

import { Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';

import type { SocieteReward, SocieteRewardCopayPrice } from '~/services/LuxLoyalty';

import { formatExtraNightRate } from './formatExtraNightRate';
import { isSocieteRewardEligible } from './isSocieteRewardEligible';
import {
  classicRewardsMargin,
  formatMarginPercent,
  formatVppCents,
  valuePerPointCents,
  vppWindowState,
} from './societeRewardMargin';

type Props = {
  /** The Société reward to summarise, or null when the package has not been priced yet. */
  reward: SocieteReward | null;
  /** Per-currency copay prices for the reward's package. */
  prices: Array<SocieteRewardCopayPrice>;
  /** Renders a tighter layout (smaller spacing/typography) when used inside a compact list row. */
  dense?: boolean;
  /**
   * Package's original trading margin percentage (0-100, addon-aware) — svc-offer's
   * `margin_percentage` on the package JSON. Rendered as a caption (1dp); omitted entirely when
   * null/undefined.
   */
  marginPercentage?: number | null;
  /**
   * AUD cost-of-goods-sold for the package — computes the live "Reward margin" caption (from the
   * reward's current `points_base` and the AUD price row's current `societe_reward_copay_base`,
   * i.e. the package's ACTUAL current values, not a form/override preview). Omit entirely
   * (`undefined`) to skip that line altogether (e.g. when this summary is shown without pricing
   * context); pass `null` to show the line as "n/a" (e.g. the package doesn't sell in AUD).
   */
  cogsAud?: number | null;
  /**
   * AUD LuxPlus+ price for the package — computes the live "VPP" caption, same live-value basis
   * as `cogsAud` above. Omit entirely (`undefined`) to skip that line; pass `null` for "n/a".
   */
  luxPlusPriceAud?: number | null;
  /** Flash config `minMarginPercent` (0-100): "Reward margin" below it renders in the warning color. `null`/omitted: no warning. */
  minMarginPercent?: number | null;
  /** Flash config `vppFloorCents` — with `vppCeilingCents`, drives the VPP caption's out-of-window warning. `null`/omitted: no window hint. */
  vppFloorCents?: number | null;
  /** Flash config `vppCeilingCents` — see `vppFloorCents`. */
  vppCeilingCents?: number | null;
};

/**
 * Maps a Société reward status to the MUI Chip color used to badge it.
 * Either eligible value (AUTO_ELIGIBLE, ADMIN_ELIGIBLE) -> success, ADMIN_INELIGIBLE -> error,
 * AUTO_INELIGIBLE -> default.
 */
function statusChipColor(status: SocieteReward['status']): 'success' | 'default' | 'error' {
  if (isSocieteRewardEligible(status)) return 'success';
  if (status === 'ADMIN_INELIGIBLE') return 'error';
  return 'default';
}

/**
 * The reason tooltip for the status chip: the pricing formula's `computed_ineligible_reason`,
 * framed as formula context rather than the live reason whenever an admin override means the
 * live status disagrees with what the formula said (`ADMIN_ELIGIBLE` with a formula reason on
 * file). Returns `null` when there's no reason to show.
 */
function ineligibleReasonTooltip(reward: SocieteReward): string | null {
  if (!reward.computed_ineligible_reason) return null;
  if (reward.status === 'ADMIN_ELIGIBLE') {
    return `Formula: ${reward.computed_ineligible_reason} — overridden by admin`;
  }
  return reward.computed_ineligible_reason;
}

/**
 * Renders one row of the per-currency copay table for a Société reward.
 * `societe_reward_copay_extra_night` of `null` renders as "— (no nightly price)" (no nightly
 * copay configured) which is distinct from `0` (an explicit zero nightly copay), rendered as "0".
 */
function CopayRow({ price }: { price: SocieteRewardCopayPrice }) {
  const extraNightDisplay =
    price.societe_reward_copay_extra_night === null
      ? '— (no nightly price)'
      : String(price.societe_reward_copay_extra_night);
  return (
    <TableRow>
      <TableCell>{price.currency_code}</TableCell>
      <TableCell>{price.societe_reward_copay_base ?? '—'}</TableCell>
      <TableCell>{extraNightDisplay}</TableCell>
    </TableRow>
  );
}

/**
 * Admin-facing summary of a package's Société flash-hotel reward: status, points, per-currency
 * copay prices, and — when the reward has been manually overridden — the override badge and the
 * frozen "formula value as of" shadow fields the override replaced.
 *
 * Renders "Not priced yet" when `reward` is null (the package has no computed reward).
 */
export default function SocieteRewardSummary({
  reward,
  prices,
  dense = false,
  marginPercentage,
  cogsAud,
  luxPlusPriceAud,
  minMarginPercent = null,
  vppFloorCents = null,
  vppCeilingCents = null,
}: Props) {
  const spacing = dense ? 0.75 : 1.5;

  if (!reward) {
    return (
      <Typography variant="body2" color="text.secondary">
        Not priced yet
      </Typography>
    );
  }

  const computedAtDisplay = new Date(reward.computed_at).toLocaleString();

  // Live reward margin / VPP: computed from the package's ACTUAL current points/co-pay (not a
  // form/override preview — there's no form here). `undefined` cogsAud/luxPlusPriceAud means the
  // caller doesn't want these lines at all (e.g. the override dialog's own embedded summary,
  // which already shows a formula-vs-override comparison further down); `null` means show "n/a".
  const audPrice = prices.find((price) => price.currency_code === 'AUD');
  const liveCopayAud = audPrice?.societe_reward_copay_base ?? null;
  const rewardMargin =
    cogsAud !== undefined
      ? classicRewardsMargin({ cogsAud, points: reward.points_base, copayAud: liveCopayAud })
      : null;
  const rewardMarginBelowThreshold =
    rewardMargin !== null && minMarginPercent !== null && rewardMargin * 100 < minMarginPercent;
  // VPP mirrors the pricing service's own formula (LP+ AUD × 100 / points, no copay term), so
  // the number shown is the one the service gated against the configured VPP window.
  const vpp =
    luxPlusPriceAud !== undefined ? valuePerPointCents({ luxPlusPriceAud, points: reward.points_base }) : null;
  const vppWindow = vppWindowState({ vppCents: vpp, vppFloorCents, vppCeilingCents });

  return (
    <Stack spacing={spacing}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        {ineligibleReasonTooltip(reward) ? (
          <Tooltip title={ineligibleReasonTooltip(reward)}>
            <Chip label={reward.status} color={statusChipColor(reward.status)} size="small" />
          </Tooltip>
        ) : (
          <Chip label={reward.status} color={statusChipColor(reward.status)} size="small" />
        )}
        {reward.is_manual_override ? (
          reward.override_meta ? (
            <Tooltip
              // `by` only comes on the admin-only overrides listing; the offer GET omits it, so
              // this tooltip names the admin only when it has one (the Overrides tab always does).
              title={`Overridden${reward.override_meta.by ? ` by ${reward.override_meta.by}` : ''} at ${new Date(
                reward.override_meta.at,
              ).toLocaleString()} — ${reward.override_meta.reason}`}
            >
              <Chip label="Overridden" size="small" variant="outlined" />
            </Tooltip>
          ) : (
            <Chip label="Overridden" size="small" variant="outlined" />
          )
        ) : null}
      </Stack>

      <Stack direction="row" spacing={2} flexWrap="wrap">
        <Typography variant="body2">Base points: {reward.points_base}</Typography>
        <Typography variant="body2">Extra-night points: {formatExtraNightRate(reward.points_extra_night)}</Typography>
      </Stack>

      {reward.is_manual_override && reward.override_meta ? (
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {/* Override meta fields are rendered verbatim — never switched on known values — so an
                unanticipated future field (e.g. "copay:EUR") still displays correctly. */}
            {reward.override_meta.fields.map((field) => (
              <Chip key={field} label={field} size="small" variant="outlined" />
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Formula value as of {computedAtDisplay}: {reward.computed_points_base} base /{' '}
            {formatExtraNightRate(reward.computed_points_extra_night)} extra-night points
          </Typography>
        </Stack>
      ) : null}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Currency</TableCell>
            <TableCell>Base copay</TableCell>
            <TableCell>Extra-night copay</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {prices.map((price) => (
            <CopayRow key={price.currency_code} price={price} />
          ))}
        </TableBody>
      </Table>

      {marginPercentage != null && (
        <Typography variant="caption" color="text.secondary">
          Package margin: {marginPercentage.toFixed(1)}%
        </Typography>
      )}
      {cogsAud !== undefined && (
        <Typography variant="caption" color={rewardMarginBelowThreshold ? 'warning.main' : 'text.secondary'}>
          Reward margin: {formatMarginPercent(rewardMargin)}
        </Typography>
      )}
      {luxPlusPriceAud !== undefined && (
        <Typography
          variant="caption"
          color={vppWindow === 'below_floor' || vppWindow === 'above_ceiling' ? 'warning.main' : 'text.secondary'}
        >
          VPP: {formatVppCents(vpp)}
          {vppWindow === 'below_floor' && ` (below ${vppFloorCents}¢ floor)`}
          {vppWindow === 'above_ceiling' && ` (above ${vppCeilingCents}¢ ceiling)`}
        </Typography>
      )}

      <Typography variant="caption" color="text.secondary">
        Formula version: {reward.formula_version}
      </Typography>
    </Stack>
  );
}
